import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function formatApiErrorDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

// ---- Global axios interceptor: auto-refresh on 401 ----
// Ensures cookies are sent on every axios call so we don't have to pass
// { withCredentials: true } in every request.
axios.defaults.withCredentials = true;

let isRefreshing = false;
let refreshWaiters = [];

const resolveWaiters = (error) => {
  refreshWaiters.forEach((cb) => cb(error));
  refreshWaiters = [];
};

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Normalize FastAPI 422 validation errors so `err.response.data.detail`
    // is always a renderable string. Without this, callers doing
    // `toast.error(err.response.data.detail)` render an array of
    // {type, loc, msg, ...} objects and React throws
    // "Objects are not valid as a React child", producing a white screen.
    if (error.response?.data && error.response.data.detail !== undefined) {
      const d = error.response.data.detail;
      if (Array.isArray(d) || (d && typeof d === 'object')) {
        error.response.data.detail = formatApiErrorDetail(d);
      }
    }

    const originalRequest = error.config || {};
    const status = error.response?.status;
    const url = originalRequest.url || '';

    // Skip refresh logic for auth endpoints AND /auth/me
    // (/auth/me is the "am I logged in?" check — a 401 is a valid outcome,
    //  not an error that needs a refresh attempt)
    const isAuthEndpoint =
      url.includes('/auth/login') ||
      url.includes('/auth/register') ||
      url.includes('/auth/refresh') ||
      url.includes('/auth/logout') ||
      url.includes('/auth/me');

    if (status !== 401 || originalRequest._retry || isAuthEndpoint) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        refreshWaiters.push((err) => {
          if (err) reject(err);
          else resolve(axios(originalRequest));
        });
      });
    }

    isRefreshing = true;
    try {
      await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
      isRefreshing = false;
      resolveWaiters(null);
      return axios(originalRequest);
    } catch (refreshError) {
      isRefreshing = false;
      resolveWaiters(refreshError);
      // Notify AuthProvider that the session is dead. It will setUser(false),
      // which triggers ProtectedRoute → Navigate("/login") via React Router.
      // We avoid window.location.href because a hard redirect races with the
      // login submit flow and can bounce users back to /login after a
      // successful login.
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('auth:session-expired'));
      }
      return Promise.reject(refreshError);
    }
  }
);
// ---- end interceptor ----

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();

    // When the interceptor detects a dead session (refresh failed), mark user
    // as logged out. ProtectedRoute will handle the client-side redirect.
    const onSessionExpired = () => setUser(false);
    window.addEventListener('auth:session-expired', onSessionExpired);
    return () => window.removeEventListener('auth:session-expired', onSessionExpired);
  }, []);

  const checkAuth = async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
    } catch (error) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      setUser(data);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (userData) => {
    try {
      const { data } = await axios.post(
        `${API}/auth/register`,
        userData,
        { withCredentials: true }
      );
      setUser(data);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      setUser(false);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};
